import { useState } from 'react';
import moize from 'moize';
import { FundCategoryFilters } from '../components/ScreenerFilters/util';

const categoryFilters = FundCategoryFilters;

const getPartiallySelectedCategories = moize((selectedSubCategories) =>
    Object.keys(categoryFilters).reduce(
        (acc, category) => {
            const subsSelected = categoryFilters[category].filter((sub) =>
                selectedSubCategories.has(sub)
            );
            return subsSelected.length
                ? { ...acc, [category]: subsSelected }
                : acc;
        },

        {}
    )
);

const categoryPartiallyChecked = moize((category, selectedSubCategories) =>
    Boolean(getPartiallySelectedCategories(selectedSubCategories)[category])
);

const categoryCompletelyChecked = (category, selectedSubCategories) =>
    categoryPartiallyChecked(category, selectedSubCategories) &&
    categoryFilters[category].every((s) => selectedSubCategories.has(s));

const generateReset = moize((defaultState) => new Set(defaultState));

const useCategoryState = (defaults) => {
    const reset = generateReset(defaults.filters);
    const defaultCategories = getPartiallySelectedCategories(reset);

    const [selectedSubCategories, setSelectedSubCategories] = useState(reset);

    const resetState = () => setSelectedSubCategories(reset);

    const addAllChildren = (category) => {
        const newState = new Set(selectedSubCategories);
        categoryFilters[category].map((s) => newState.add(s));
        setSelectedSubCategories(newState);
    };

    const removeAllChildren = (category) => {
        const newState = new Set(selectedSubCategories);
        categoryFilters[category].map((s) => newState.delete(s));
        setSelectedSubCategories(newState);
    };

    const toggleAllChildren = (category) =>
        categoryCompletelyChecked(category, selectedSubCategories)
            ? removeAllChildren(category)
            : addAllChildren(category);

    const addSubCategory = (subCategory) => {
        const newState = new Set(selectedSubCategories);
        newState.add(subCategory);
        setSelectedSubCategories(newState);
    };

    const addSubCategories = (subCategories) => {
        const newState = new Set(selectedSubCategories);
        subCategories.forEach((s) => newState.add(s));
        setSelectedSubCategories(newState);
    };

    const removeSubCategory = (subCategory) => {
        const newState = new Set(selectedSubCategories);
        newState.delete(subCategory);
        setSelectedSubCategories(newState);
    };

    const removeAllSubCategory = () => {
        setSelectedSubCategories(new Set());
    };

    const toggleCategoryDefaults = (cat) =>
        categoryPartiallyChecked(cat, selectedSubCategories)
            ? removeAllChildren(cat)
            : addSubCategories(defaultCategories[cat]);

    const toggleSubCategory = (subCategory) =>
        selectedSubCategories.has(subCategory)
            ? removeSubCategory(subCategory)
            : addSubCategory(subCategory);

    const partiallySelectedCategories = getPartiallySelectedCategories(
        selectedSubCategories
    );
    const api = {
        resetState,
        addAllChildren,
        addSubCategories,
        toggleCategoryDefaults,
        removeAllChildren,
        categoryCompletelyChecked: (c) =>
            categoryCompletelyChecked(c, selectedSubCategories),
        categoryPartiallyChecked: (c) =>
            categoryPartiallyChecked(c, selectedSubCategories),
        toggleAllChildren,
        addSubCategory,
        removeSubCategory,
        removeAllSubCategory,
        toggleSubCategory,
        partiallySelectedCategories,
        defaults,
        defaultCategories
    };
    return { selectedSubCategories, ...api };
};

export default useCategoryState;
